import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { enUS, es } from 'date-fns/locale';
import { Box, Button, CircularProgress, Pagination, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';
import AlertBlock from 'components/shared/blocks/AlertBlock';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import SessionFilters from 'components/session/SessionFilters';
import SessionListItem from 'components/session/SessionListItem';
import SessionMarkMenu from 'components/session/SessionMarkMenu';
import TrainerSessionsDashboard from 'components/session/TrainerSessionsDashboard';
import StatusGroupHeader, { STATUS_GROUP_META, STATUS_GROUP_ORDER } from 'components/session/SessionStatusGroupHeader';
import { sessionStatus } from 'common/constants/options';
import { timeFormat } from 'common/constants/date';
import { convertUtcToLocalTime } from 'common/utils/date';
import { normalizeSessionStatus } from 'common/utils/sessionColor';
import { isAdmin, isSupervisor, isTrainer } from 'common/utils/user';
import routes from 'common/routes';
import { buildPathWithId } from 'common/utils/id';
import AppBarContext from 'context/AppBarContext';
import sessionSlice from 'store/modules/session';

const CLIENTS = gql`
  query SessionClients {
    users(role: CLIENT, first: 1000, page: 1, orderBy: { column: FIRST_NAME, order: ASC }) {
      data { id full_name }
    }
  }
`;

const TRAINERS = gql`
  query SessionTrainers {
    users(role: TRAINER, first: 1000, page: 1, orderBy: { column: FIRST_NAME, order: ASC }) {
      data { id full_name }
    }
  }
`;

const PER_PAGE = 25;
const INITIAL_APPLIED = { clientId: '', trainerId: '', status: '', timeRange: '', date: {} };
const rangeLabel = (s) => `${convertUtcToLocalTime(s.start_date, timeFormat)} – ${convertUtcToLocalTime(s.end_date, timeFormat)}`;

function AdminSessionList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation(['labels', 'routes', 'sessions', 'tables']);
  const dateLocale = i18n.language === 'es' ? es : enUS;
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const role = useSelector((state) => state.auth.user.role);
  const userId = useSelector((state) => state.auth.user.id);
  const sessions = useSelector((state) => state.session.data.list);
  const status = useSelector((state) => state.session.status.general);
  const paginatorInfo = useSelector((state) => state.session.paginatorInfo);
  const refresh = useSelector((state) => state.session.refresh);

  const adminMode = isAdmin(role) || isSupervisor(role);
  const isTrainerUser = isTrainer(role);
  // OJO: el borrado es SOLO del administrador. `SessionPolicy@forceDelete` en el backend admite
  // admin o el propio cliente dueño de la sesión, así que el supervisor (que sí ve el listado)
  // recibiría "permiso denegado" y no debe ver las acciones.
  const canDelete = isAdmin(role);
  const pageTitle = t(adminMode ? 'session.list' : 'session.mySessions', { ns: 'routes' });

  const [applied, setApplied] = useState(INITIAL_APPLIED);
  const [page, setPage] = useState(1); // server-side, 1-based
  // Borrado (admin): confirmación siempre, y quita optimista mientras llega la lista recargada.
  const [deleteId, setDeleteId] = useState(null);
  const [deletedIds, setDeletedIds] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const { data: clientsData } = useQuery(CLIENTS, { fetchPolicy: 'cache-first' });
  const clientOptions = useMemo(
    () => (clientsData?.users?.data ?? []).map((c) => ({ value: c.id, label: c.full_name })),
    [clientsData]
  );

  const { data: trainersData } = useQuery(TRAINERS, { fetchPolicy: 'cache-first' });
  const trainerOptions = useMemo(
    () => (trainersData?.users?.data ?? []).map((tr) => ({ value: tr.id, label: tr.full_name })),
    [trainersData]
  );

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const onApply = useCallback((values) => { setApplied(values); setPage(1); }, []);

  // Al cambiar de página o de filtros la selección deja de tener sentido (los ids ya no están
  // a la vista): se descarta para no borrar nada por accidente.
  useEffect(() => { setSelectMode(false); setSelectedIds([]); }, [applied, page]);

  // Admin: TODAS las sesiones. Entrenador: solo las suyas (trainer_id=self). Ambos: paginadas en
  // servidor, filtro de cliente + estado (ENUM). OJO: client_id/trainer_id van como ESCALAR
  // (no array de ids) porque getSessionFilters usa getIdsFromOptions (espera {value}).
  useEffect(() => {
    if (isTrainerUser && !userId) return;
    dispatch(sessionSlice.actions.list({
      pagination: { first: PER_PAGE, page },
      filters: {
        client_id: applied.clientId || '',
        // Entrenador: si el rol es entrenador se fuerza el propio; el admin filtra por el elegido.
        trainer_id: isTrainerUser ? userId : (applied.trainerId || ''),
        status: applied.status || '',
        start_between: { start_date: applied.date?.start_date || null, end_date: applied.date?.end_date || null }
      },
      orderBy: { column: 'START_DATE', direction: 'DESC' }
    }));
  }, [adminMode, isTrainerUser, userId, applied, page, dispatch, refresh]);

  // La ocultación optimista vive solo hasta que llega la lista recargada: si el borrado falló
  // (sale el snackbar de error), la sesión reaparece en vez de quedar oculta hasta recargar.
  useEffect(() => { setDeletedIds((prev) => (prev.length ? [] : prev)); }, [sessions]);

  const items = useMemo(() => sessions
    .filter((s) => !deletedIds.includes(String(s.id)))
    .map((s) => ({ session: s, norm: normalizeSessionStatus(s.status, s.start_date) })), [sessions, deletedIds]);

  const timeRangeOptions = useMemo(() => {
    const set = new Set();
    items.forEach(({ session: s }) => set.add(rangeLabel(s)));
    return [...set].sort();
  }, [items]);

  // Estados como ENUM del servidor (1:1) para que el filtro funcione con la paginación.
  const statusOptions = [
    { value: 'all', label: t('allStatuses', { ns: 'sessions', defaultValue: 'Todos los estados' }) },
    { value: sessionStatus.scheduled, label: t('statusScheduledPlural', { ns: 'sessions', defaultValue: 'Programadas' }) },
    { value: sessionStatus.finished, label: t('statusCompletedPlural', { ns: 'sessions', defaultValue: 'Completadas' }) },
    { value: sessionStatus.clientAbsent, label: t('markClientAbsent', { ns: 'sessions', defaultValue: 'Cliente ausente' }) },
    { value: sessionStatus.trainerAbsent, label: t('markTrainerAbsent', { ns: 'sessions', defaultValue: 'Entrenador ausente' }) }
  ];

  // Horario es el único filtro client-side (sobre la página actual). Se agrupa por estado
  // (programadas → pendientes → completadas → ausencias) y dentro de cada grupo, recientes primero.
  const visibleItems = useMemo(() => {
    const filtered = applied.timeRange ? items.filter(({ session: s }) => rangeLabel(s) === applied.timeRange) : items;
    return [...filtered].sort((a, b) => {
      const oa = STATUS_GROUP_ORDER[a.norm] ?? 4;
      const ob = STATUS_GROUP_ORDER[b.norm] ?? 4;
      if (oa !== ob) return oa - ob;
      return new Date(b.session.start_date) - new Date(a.session.start_date);
    });
  }, [items, applied.timeRange]);

  const onMark = useCallback((id, newStatus) => {
    dispatch(sessionSlice.actions.update({ id, input: { status: newStatus } }));
  }, [dispatch]);

  // El admin puede eliminar CUALQUIER sesión (no aplica la ventana de 8 h del cliente): es una
  // acción de mantenimiento sobre la agenda, no una cancelación con reglas de negocio.
  const selectableIds = useMemo(() => visibleItems.map(({ session: s }) => String(s.id)), [visibleItems]);
  const toggleSelect = useCallback((id) => {
    const key = String(id);
    setSelectedIds((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  }, []);
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => (prev.length === selectableIds.length ? [] : selectableIds));
  }, [selectableIds]);
  const exitSelectMode = useCallback(() => { setSelectMode(false); setSelectedIds([]); }, []);

  const confirmDelete = useCallback(() => {
    if (deleteId == null) return;
    dispatch(sessionSlice.actions.forceDelete([deleteId]));
    setDeletedIds((prev) => [...prev, String(deleteId)]);
    setDeleteId(null);
  }, [deleteId, dispatch]);

  const confirmBulkDelete = useCallback(() => {
    if (selectedIds.length === 0) return;
    dispatch(sessionSlice.actions.forceDelete(selectedIds));
    setDeletedIds((prev) => [...prev, ...selectedIds]);
    setSelectedIds([]);
    setSelectMode(false);
    setBulkDeleteOpen(false);
  }, [selectedIds, dispatch]);

  const isLoading = status === 'loading' && sessions.length === 0;
  const total = paginatorInfo?.total || 0;
  // OJO: el query de `sessions` NO devuelve `lastPage` (solo total) → se calcula desde total.
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {adminMode
          ? t('adminAllSessionsSubtitle', { ns: 'sessions', defaultValue: 'Todas las sesiones. Busca un cliente para filtrar.' })
          : t('trainerSessionsSubtitle', { ns: 'sessions', defaultValue: 'Revisa y reporta tus sesiones individuales.' })}
      </Typography>

      <SessionFilters
        clientOptions={clientOptions}
        trainerOptions={trainerOptions}
        timeRangeOptions={timeRangeOptions}
        statusOptions={statusOptions}
        onApply={onApply}
      />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2, mb: 1, flexWrap: 'wrap', gap: 1 }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('totalSessions', { ns: 'sessions', count: total, defaultValue: '{{count}} sesiones en total' })}
        </Typography>

        {canDelete && visibleItems.length > 0 && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {!selectMode ? (
              <Button
                size="small"
                variant="text"
                color="inherit"
                startIcon={<DeleteIcon fontSize="small" />}
                onClick={() => setSelectMode(true)}
                sx={{ color: 'secondary.main', textTransform: 'none', fontWeight: 700 }}
              >
                {t('selectToDelete', { ns: 'sessions', defaultValue: 'Seleccionar para eliminar' })}
              </Button>
            ) : (
              <>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('selectedCount', { ns: 'sessions', count: selectedIds.length, defaultValue: `${selectedIds.length} seleccionadas` })}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={toggleSelectAll}
                  sx={{ color: 'text.primary', borderColor: 'hairline.strong', textTransform: 'none', fontWeight: 700, '&:hover': { borderColor: 'hairline.strong', backgroundColor: 'action.hover' } }}
                >
                  {selectedIds.length === selectableIds.length
                    ? t('clearSelection', { ns: 'sessions', defaultValue: 'Quitar selección' })
                    : t('selectAll', { ns: 'sessions', defaultValue: 'Seleccionar todas' })}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  disabled={selectedIds.length === 0}
                  startIcon={<DeleteIcon fontSize="small" />}
                  onClick={() => setBulkDeleteOpen(true)}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  {t('deleteSelected', { ns: 'sessions', defaultValue: 'Eliminar' })}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={exitSelectMode}
                  sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                  {t('cancel', { ns: 'sessions', defaultValue: 'Cancelar' })}
                </Button>
              </>
            )}
          </Stack>
        )}
      </Stack>

      {isTrainerUser && <AlertBlock type="sessionUpdate" />}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: 'secondary.main' }} /></Box>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {visibleItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body2" color="text.secondary">{t('empty', { type: pageTitle.toLowerCase(), ns: 'tables' })}</Typography>
            </Box>
          ) : (() => {
            // Encabezado-divisor al cambiar de grupo de estado (la lista ya viene agrupada).
            let lastGroup = null;
            const nodes = [];
            visibleItems.forEach(({ session: s, norm }) => {
              if (norm !== lastGroup) {
                const meta = STATUS_GROUP_META[norm] || STATUS_GROUP_META.scheduled;
                nodes.push(
                  <StatusGroupHeader
                    key={`grp-${norm}-${s.id}`}
                    label={t(meta.key, { ns: 'sessions', defaultValue: meta.def })}
                    color={theme.palette.sessionStatus[meta.path].fg}
                    first={nodes.length === 0}
                  />
                );
                lastGroup = norm;
              }
              nodes.push(
                <SessionListItem
                  key={s.id}
                  session={s}
                  dateLocale={dateLocale}
                  person={s.client_id}
                  actionLabel={!selectMode && adminMode ? t('edit', { ns: 'sessions', defaultValue: 'Editar' }) : null}
                  // En modo selección la card entera marca/desmarca (en vez de navegar a editar).
                  onAction={(() => {
                    if (selectMode) return () => toggleSelect(s.id);
                    if (!adminMode) return undefined;
                    // Sin id válido no se navega: llevaba a "/sessions/edit/undefined", una
                    // pantalla en blanco sin mensaje.
                    const editPath = buildPathWithId(routes.editSession.path, s.id);
                    return editPath ? () => navigate(editPath) : undefined;
                  })()}
                  onDelete={canDelete && !selectMode ? () => setDeleteId(s.id) : undefined}
                  selectable={canDelete && selectMode}
                  checked={selectedIds.includes(String(s.id))}
                  onToggleSelect={() => toggleSelect(s.id)}
                  actionNode={isTrainerUser ? (
                    <SessionMarkMenu onMark={(st) => onMark(s.id, st)} disabled={s.status !== sessionStatus.scheduled} />
                  ) : null}
                />
              );
            });
            return nodes;
          })()}
        </Stack>
      )}

      {lastPage > 1 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination count={lastPage} page={page} onChange={(_, p) => setPage(p)} color="secondary" shape="rounded" />
        </Stack>
      )}

      <ConfirmDeleteModal
        open={deleteId != null}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        title={t('deleteSessionTitle', { ns: 'sessions', defaultValue: 'Eliminar sesión' })}
        message={t('deleteSessionAdminMessage', { ns: 'sessions', defaultValue: '¿Seguro que quieres eliminar esta sesión? Se le liberará el cupo al cliente.' })}
        count={1}
      />
      <ConfirmDeleteModal
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={confirmBulkDelete}
        title={t('deleteSessionsTitle', { ns: 'sessions', defaultValue: 'Eliminar sesiones' })}
        message={t('deleteSessionsAdminMessage', { ns: 'sessions', count: selectedIds.length, defaultValue: '¿Seguro que quieres eliminar las sesiones seleccionadas? Se les liberarán los cupos a los clientes.' })}
        count={selectedIds.length}
      />
    </>
  );
}

/**
 * Punto de entrada de la ruta de sesiones (admin/supervisor/entrenador). El ENTRENADOR ve el
 * dashboard "Mis sesiones" (counters + filtros + panel lateral con donut + Marcar como); el resto
 * ve el listado de administración. El rol no cambia en vivo, así que el branch es estable.
 */
function SessionList() {
  const role = useSelector((state) => state.auth.user.role);
  if (isTrainer(role)) return <TrainerSessionsDashboard />;
  return <AdminSessionList />;
}

export default SessionList;
